/**
 * R7 client analysis — one truthful multi-metric history model.
 *
 * Every value comes from the selected sector's own telemetry series. Values are
 * bucketed by taking the LAST real reading in each bucket; nothing is averaged,
 * interpolated or carried forward. Water volume is derived only from calibrated
 * flow readings and skips gaps longer than three hours, matching the backend's
 * conservative water-balance rule while refusing to turn an absent flow metric
 * into a synthetic zero.
 */

import type { TelemetryPoint } from '../../lib/operationalOverview';
import {
  getAirHumidity,
  getAirTemperature,
  getBattery,
  getFlow,
  getPressure,
  getSoilEc,
  getSoilMoisture,
  getSoilTemperature,
  type Measures,
} from '../../lib/liveSensorMetrics';
import { bucketMsFor } from './sectorHistory';
import { lpmToM3h } from './agriSafeDashboardAdapter';

export type AnalysisMetricKey =
  | 'soilMoisture'
  | 'soilTemperature'
  | 'soilEc'
  | 'airTemperature'
  | 'airHumidity'
  | 'pressure'
  | 'flow'
  | 'battery';

export interface AnalysisMetricDefinition {
  key: AnalysisMetricKey;
  label: string;
  unit: string;
  digits: number;
  read: (measures: Measures | undefined) => number | undefined;
}

export const ANALYSIS_METRICS: readonly AnalysisMetricDefinition[] = [
  { key: 'pressure', label: 'Pression', unit: 'bar', digits: 2, read: getPressure },
  {
    key: 'flow', label: 'Débit', unit: 'm³/h', digits: 2,
    read: (measures) => {
      const lpm = getFlow(measures);
      return lpm === undefined ? undefined : lpmToM3h(lpm);
    },
  },
  { key: 'soilMoisture', label: 'Humidité sol', unit: '%', digits: 1, read: getSoilMoisture },
  { key: 'soilTemperature', label: 'Temp. sol', unit: '°C', digits: 1, read: getSoilTemperature },
  { key: 'soilEc', label: 'EC sol', unit: 'µS/cm', digits: 0, read: getSoilEc },
  { key: 'airTemperature', label: 'Temp. air', unit: '°C', digits: 1, read: getAirTemperature },
  { key: 'airHumidity', label: 'Humidité air', unit: '%', digits: 1, read: getAirHumidity },
  { key: 'battery', label: 'Batterie', unit: 'V', digits: 2, read: getBattery },
] as const;

export type AnalysisHistoryPoint = {
  timestamp: string;
} & Partial<Record<AnalysisMetricKey, number>>;

export interface AnalysisSeriesInput {
  series: TelemetryPoint[];
  metricKeys: readonly AnalysisMetricKey[];
}

const timestampMs = (point: TelemetryPoint): number | null => {
  if (!point.ts) return null;
  const parsed = Date.parse(point.ts);
  return Number.isFinite(parsed) ? parsed : null;
};

const DEFINITION_BY_KEY = new Map(ANALYSIS_METRICS.map((definition) => [definition.key, definition]));

export function buildAnalysisHistory(
  inputs: AnalysisSeriesInput[],
  window: { startMs: number; endMs: number },
): AnalysisHistoryPoint[] {
  const bucketMs = bucketMsFor(window.endMs - window.startMs);
  const buckets = new Map<number, AnalysisHistoryPoint>();
  const latestTimestamp = new Map<string, number>();

  for (const input of inputs) {
    const ordered = [...input.series].sort(
      (a, b) => (timestampMs(a) ?? 0) - (timestampMs(b) ?? 0),
    );
    for (const point of ordered) {
      const ms = timestampMs(point);
      if (ms === null || ms < window.startMs || ms > window.endMs) continue;
      const bucketStart = Math.floor(ms / bucketMs) * bucketMs;
      let bucket = buckets.get(bucketStart);
      if (!bucket) {
        bucket = { timestamp: new Date(bucketStart).toISOString() };
        buckets.set(bucketStart, bucket);
      }

      for (const key of input.metricKeys) {
        const definition = DEFINITION_BY_KEY.get(key);
        if (!definition) continue;
        const value = definition.read(point.measures ?? undefined);
        if (value === undefined || !Number.isFinite(value)) continue;
        const stampKey = `${bucketStart}:${key}`;
        if (ms < (latestTimestamp.get(stampKey) ?? Number.NEGATIVE_INFINITY)) continue;
        bucket[key] = value;
        latestTimestamp.set(stampKey, ms);
      }
    }
  }

  return Array.from(buckets.entries())
    .sort(([a], [b]) => a - b)
    .map(([, point]) => point);
}

export function availableAnalysisMetrics(points: AnalysisHistoryPoint[]): AnalysisMetricKey[] {
  return ANALYSIS_METRICS
    .map((definition) => definition.key)
    .filter((key) => points.some((point) => typeof point[key] === 'number' && Number.isFinite(point[key])));
}

export function latestAnalysisMetric(
  points: AnalysisHistoryPoint[],
  key: AnalysisMetricKey,
): number | null {
  for (let index = points.length - 1; index >= 0; index -= 1) {
    const value = points[index][key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
}

export interface MeasuredVolume {
  volumeM3: number | null;
  integratedMinutes: number;
}

/**
 * Integrate calibrated L/min flow over real sample intervals.
 *
 * Both endpoints must carry a real flow value. A gap > 3 h is excluded rather
 * than assuming a constant flow through missing telemetry. A true measured
 * zero remains zero. No valid interval => null, never a fabricated 0 m³.
 */
export function integrateMeasuredWaterVolume(
  series: TelemetryPoint[],
  window: { startMs: number; endMs: number },
): MeasuredVolume {
  const points = series
    .map((point) => ({
      ms: timestampMs(point),
      flowLpm: getFlow(point.measures ?? undefined),
    }))
    .filter((point): point is { ms: number; flowLpm: number } => (
      point.ms !== null
      && point.ms >= window.startMs
      && point.ms <= window.endMs
      && typeof point.flowLpm === 'number'
      && Number.isFinite(point.flowLpm)
    ))
    .sort((a, b) => a.ms - b.ms);

  let totalLiters = 0;
  let integratedMinutes = 0;
  let intervals = 0;
  const MAX_GAP_MINUTES = 180;

  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    const minutes = (current.ms - previous.ms) / 60_000;
    if (!(minutes > 0 && minutes <= MAX_GAP_MINUTES)) continue;
    totalLiters += ((previous.flowLpm + current.flowLpm) / 2) * minutes;
    integratedMinutes += minutes;
    intervals += 1;
  }

  return {
    volumeM3: intervals === 0 ? null : totalLiters / 1000,
    integratedMinutes,
  };
}
